import React, { useState, useEffect } from "react";
import { Input, Typography } from "antd";
import { UserOutlined, MailOutlined, PhoneOutlined } from "@ant-design/icons";

const { Text } = Typography;

const PersonalInfoStep = ({
  firstName,
  lastName,
  email,
  phoneNumber,
  handleFirstNameChange,
  handleLastNameChange,
  handleEmailChange,
  handlePhoneChange,
  // New prop to expose validation status to parent
  onValidationChange
}) => {
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");

  // Validate fields and notify parent
  useEffect(() => {
    const isValid = 
      firstName.trim() !== "" &&
      lastName.trim() !== "" &&
      phoneNumber.trim() !== "" &&
      !phoneError &&
      (!email || (!emailError && email.trim() !== ""));

    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [firstName, lastName, email, phoneNumber, phoneError, emailError, onValidationChange]);

  const isValidEmail = (value) => {
    if (!value || value.trim() === "") return true; // Email is optional, so empty is valid
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(value);
  };

  const isValidPhoneNumber = (value) => {
    const regex = /^\+?\d{10,15}$/; // supports +countrycode and 7-15 digits
    return regex.test(value.trim());
  };

  const isValidName = (value) => {
    return value.trim() !== "";
  };

  const handleFirstNameInput = (e) => {
    const value = e.target.value;
    handleFirstNameChange(e);
    if (!isValidName(value)) {
      setFirstNameError("First name is required");
    } else {
      setFirstNameError("");
    }
  };

  const handleLastNameInput = (e) => {
    const value = e.target.value;
    handleLastNameChange(e);
    if (!isValidName(value)) {
      setLastNameError("Last name is required");
    } else {
      setLastNameError("");
    }
  };

  const handleEmailInput = (e) => {
    const value = e.target.value;
    handleEmailChange(e);
    if (value && !isValidEmail(value)) {
      setEmailError("Invalid email format");
    } else {
      setEmailError("");
    }
  };

  const handlePhoneInput = (e) => {
    const value = e.target.value;
    handlePhoneChange(e);
    
    if (!value || value.trim() === "") {
      setPhoneError("Phone number is required");
    } else if (!isValidPhoneNumber(value)) {
      setPhoneError("Invalid phone number. Use 7-15 digits with optional +country code");
    } else {
      setPhoneError("");
    }
  };

  return (
    <div className="step-content">
      <h3>Personal Information</h3>

      <div style={{ marginBottom: 10 }}>
        <Input
          prefix={<UserOutlined />}
          placeholder="First Name *"
          value={firstName}
          onChange={handleFirstNameInput}
          className="signup-input"
          status={firstNameError ? "error" : ""}
          required
        />
        {firstNameError && <Text type="danger" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>{firstNameError}</Text>}
      </div>

      <div style={{ marginBottom: 10 }}>
        <Input
          prefix={<UserOutlined />}
          placeholder="Last Name *"
          value={lastName}
          onChange={handleLastNameInput}
          className="signup-input"
          status={lastNameError ? "error" : ""}
          required
        />
        {lastNameError && <Text type="danger" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>{lastNameError}</Text>}
      </div>

      <div style={{ marginBottom: 10 }}>
        <Input
          prefix={<MailOutlined />}
          placeholder="Email (optional)"
          value={email}
          onChange={handleEmailInput}
          className="signup-input"
          status={emailError ? "error" : ""}
        />
        {emailError && <Text type="danger" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>{emailError}</Text>}
      </div>

      <div style={{ marginBottom: 10 }}>
        <Input
          prefix={<PhoneOutlined />}
          placeholder="Phone Number *"
          value={phoneNumber}
          onChange={handlePhoneInput}
          className="signup-input"
          status={phoneError ? "error" : ""}
          required
          maxLength={12} // +countrycode + 15 digits
        />
        {phoneError && <Text type="danger" style={{ fontSize: '12px', marginTop: '4px', display: 'block' }}>{phoneError}</Text>}
      </div>

      <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
        * Required fields
      </Text>
    </div>
  );
};

export default PersonalInfoStep;